import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Loader2, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth?.() || {};

  // status: "verifying" | "success" | "error" | "no-token"
  const [status, setStatus] = useState(token ? "verifying" : "no-token");
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await http.post("/auth/verify-email", { token });
        setEmail(data?.email || "");
        setStatus("success");
        // Refresh the cached user so /dashboard sees verified status immediately.
        try { await refresh?.(); } catch (_e) { /* non-blocking */ }
      } catch (e) {
        setErrorMsg(e?.response?.data?.detail || "Verification failed. The link may have expired.");
        setStatus("error");
      }
    })();
  }, [token]);

  return (
    <main className="min-h-screen grid place-items-center bg-ink-50 px-4 py-16">
      <div
        data-testid="verify-email-card"
        className="bg-white border border-ink-200 rounded-sm p-8 max-w-md w-full text-center shadow-sm"
      >
        {status === "verifying" && (
          <>
            <Loader2 className="w-10 h-10 text-brand animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-ink-900 tracking-tight mb-2">Verifying your email…</h1>
            <p className="text-sm text-ink-600">Just a moment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-extrabold text-ink-900 tracking-tight mb-2" data-testid="verify-success-title">Email verified!</h1>
            <p className="text-sm text-ink-600 mb-6">
              {email ? <><span className="font-mono text-ink-900">{email}</span> is now confirmed.</> : "Your account is now active."} You&apos;re ready to enrich leads.
            </p>
            <Button
              data-testid="verify-go-dashboard"
              onClick={() => navigate("/dashboard")}
              className="w-full bg-brand hover:bg-brand-hover text-white rounded-sm h-10 text-[11px] uppercase tracking-widestx"
            >
              Go to dashboard →
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-14 h-14 rounded-full bg-red-50 grid place-items-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-extrabold text-ink-900 tracking-tight mb-2" data-testid="verify-error-title">Verification failed</h1>
            <p className="text-sm text-ink-600 mb-6" data-testid="verify-error-message">{errorMsg}</p>
            <Link to="/check-inbox" className="block">
              <Button className="w-full bg-brand hover:bg-brand-hover text-white rounded-sm h-10 text-[11px] uppercase tracking-widestx">
                Request a new link
              </Button>
            </Link>
          </>
        )}

        {status === "no-token" && (
          <>
            <div className="w-14 h-14 rounded-full bg-ink-100 grid place-items-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-ink-500" />
            </div>
            <h1 className="font-display text-2xl font-extrabold text-ink-900 tracking-tight mb-2">No verification token</h1>
            <p className="text-sm text-ink-600 mb-6">
              This page requires a verification link. Please click the button in your verification email.
            </p>
            <Link to="/check-inbox">
              <Button variant="outline" className="rounded-sm h-10 text-[11px] uppercase tracking-widestx">
                Resend verification email
              </Button>
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
